import { Component, inject, signal, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { DemandesService } from '../../../../core/service/demandes-service/demandes.service';
import { PlayersService } from '../../../../core/service/players-service/players-service';
import { SeasonsService, LicencePeriod } from '../../../../core/service/seasons-service/seasons.service';
import { AuthService } from '../../../../core/service/auth/auth.service';
import { PermissionService } from '../../../../core/service/permission/permission.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-transfer-demande',
  standalone: false,
  templateUrl: './transfer-demande.component.html',
})
export class TransferDemandeComponent implements OnInit {
  private demandesService = inject(DemandesService);
  private playersService = inject(PlayersService);
  private seasonsService = inject(SeasonsService);
  private router = inject(Router);
  private authService = inject(AuthService);
  permService = inject(PermissionService);
  private destroyRef = inject(DestroyRef);

  isLoading = signal(true);
  isSubmitting = signal(false);
  players = signal<any[]>([]);
  licencePeriods = signal<LicencePeriod[]>([]);
  playerSearch = signal('');

  form = {
    player_id: null as number | null,
    demande_type: 'TRANSFER',
    licenceperiod_id: null as number | null,
    motif: '',
  };

  transferTypes = [
    { value: 'TRANSFER', label: 'Transfert d\u00e9finitif' },
    { value: 'MUTATION', label: 'Mutation' },
    { value: 'PRET', label: 'Pr\u00eat' },
  ];

  get filteredPlayers(): any[] {
    const q = this.playerSearch().toLowerCase().trim();
    if (!q) return this.players();
    return this.players().filter(p =>
      (p.player_first_name + ' ' + p.player_last_name).toLowerCase().includes(q) ||
      (p.player_last_name + ' ' + p.player_first_name).toLowerCase().includes(q) ||
      (p.player_identity_number || '').toLowerCase().includes(q)
    );
  }

  ngOnInit(): void {
    if (environment.action_block) { this.router.navigate(['/']); return; }
    this.loadData();
  }

  private loadData(): void {
    const club = this.authService.getClub();
    if (!club?.club_id) {
      this.isLoading.set(false);
      return;
    }

    let playersLoaded = false;
    let periodsLoaded = false;

    const checkDone = () => {
      if (playersLoaded && periodsLoaded) {
        this.isLoading.set(false);
      }
    };

    // Load players for the club
    this.playersService.listPlayers(club.club_id, 1, 500)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.players.set(res.data || []);
          playersLoaded = true;
          checkDone();
        },
        error: () => {
          playersLoaded = true;
          checkDone();
        },
      });

    // Load licence periods from active season
    this.seasonsService.getActive()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const season = res?.data;
          if (!season?.sportseason_id) {
            periodsLoaded = true;
            checkDone();
            return;
          }
          this.seasonsService.getLicencePeriods(season.sportseason_id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (periodsRes) => {
                const periods: LicencePeriod[] = (periodsRes?.data || []).filter(
                  (p: LicencePeriod) => p.licenceperiod_status === 'OPEN'
                );
                this.licencePeriods.set(periods);
                if (periods.length > 0) {
                  this.form.licenceperiod_id = periods[0].licenceperiod_id;
                }
                periodsLoaded = true;
                checkDone();
              },
              error: () => {
                periodsLoaded = true;
                checkDone();
              },
            });
        },
        error: () => {
          periodsLoaded = true;
          checkDone();
        },
      });
  }

  submit(): void {
    if (this.isSubmitting()) return;

    if (!this.form.player_id) {
      SwalUtils.error('Champ requis', 'Veuillez s\u00e9lectionner un joueur');
      return;
    }
    if (!this.form.licenceperiod_id) {
      SwalUtils.error('Champ requis', 'Veuillez s\u00e9lectionner une p\u00e9riode de licence');
      return;
    }

    this.isSubmitting.set(true);
    this.demandesService.submit('player', {
      entity_id: this.form.player_id,
      demande_type: this.form.demande_type,
      licenceperiod_id: this.form.licenceperiod_id,
      motif: this.form.motif,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res?.success) {
            SwalUtils.success('Demande soumise', 'La demande de transfert a \u00e9t\u00e9 envoy\u00e9e avec succ\u00e8s');
            this.router.navigate(['/Demandes']);
          } else {
            SwalUtils.error('Erreur', res?.message || 'Erreur lors de la soumission de la demande');
          }
          this.isSubmitting.set(false);
        },
        error: () => {
          SwalUtils.error('Erreur', 'Erreur lors de la soumission de la demande');
          this.isSubmitting.set(false);
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/Demandes']);
  }
}
